import React, { useState } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  Popover,
  Grid,
  Button,
  Typography,
  IconButton,
  Divider,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { motion } from 'framer-motion';

/**
 * GuestSelector Component
 * Select number of adults and children for the trip
 */
const GuestSelector = ({
  adults = 1,
  children = 0,
  onGuestsChange,
  disabled = false,
  maxAdults = 8,
  maxChildren = 6,
  ...props
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [tempAdults, setTempAdults] = useState(adults);
  const [tempChildren, setTempChildren] = useState(children);

  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
      setTempAdults(adults);
      setTempChildren(children);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleApply = () => {
    onGuestsChange({
      adults: tempAdults,
      children: tempChildren,
      total: tempAdults + tempChildren,
    });
    handleClose();
  };

  const handleReset = () => {
    setTempAdults(1);
    setTempChildren(0);
  };

  const displayText = `${adults + children} Guest${adults + children !== 1 ? 's' : ''}`;

  const Counter = ({ label, value, onChange, min = 0, max = 10 }) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 0',
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton
          size="small"
          onClick={() => onChange(Math.max(min, value - 1))}
          disabled={value <= min}
          sx={{
            color: 'primary.main',
            '&:hover': {
              backgroundColor: 'rgba(0, 59, 149, 0.1)',
            },
          }}
        >
          <RemoveIcon fontSize="small" />
        </IconButton>
        <TextField
          type="number"
          value={value}
          onChange={(e) => {
            const newVal = Math.max(min, Math.min(max, parseInt(e.target.value) || 0));
            onChange(newVal);
          }}
          inputProps={{ min, max, style: { textAlign: 'center' } }}
          sx={{
            width: '60px',
            '& input': {
              textAlign: 'center',
              fontWeight: 600,
            },
          }}
          size="small"
        />
        <IconButton
          size="small"
          onClick={() => onChange(Math.min(max, value + 1))}
          disabled={value >= max}
          sx={{
            color: 'primary.main',
            '&:hover': {
              backgroundColor: 'rgba(0, 59, 149, 0.1)',
            },
          }}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );

  return (
    <>
      <TextField
        {...props}
        value={displayText}
        placeholder="Select guests"
        disabled={disabled}
        fullWidth
        variant="outlined"
        size="medium"
        readOnly
        onClick={handleClick}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <PersonIcon sx={{ color: 'primary.main' }} />
            </InputAdornment>
          ),
        }}
        sx={{
          cursor: 'pointer',
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            backgroundColor: 'white',
            transition: 'all 0.3s ease',
            
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0, 59, 149, 0.1)',
            },
            
            '&.Mui-focused': {
              boxShadow: '0 6px 20px rgba(0, 59, 149, 0.15)',
            },
          },
        }}
      />

      {/* Guest Selection Popover */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            borderRadius: '8px',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
            minWidth: '320px',
            overflow: 'visible',
            marginTop: '8px',
          },
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
        >
          <Box sx={{ padding: '20px' }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                marginBottom: '16px',
                color: 'text.primary',
              }}
            >
              Select Guests
            </Typography>

            {/* Counters */}
            <Box>
              <Counter
                label="Adults (13+ years)"
                value={tempAdults}
                onChange={setTempAdults}
                min={1}
                max={maxAdults}
              />
              <Divider sx={{ my: 1 }} />
              <Counter
                label="Children (0-12 years)"
                value={tempChildren}
                onChange={setTempChildren}
                min={0}
                max={maxChildren}
              />
            </Box>

            {/* Summary */}
            <Box
              sx={{
                backgroundColor: 'rgba(0, 59, 149, 0.05)',
                borderRadius: '6px',
                padding: '12px',
                marginTop: '16px',
                marginBottom: '16px',
              }}
            >
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Total Guests: <strong>{tempAdults + tempChildren}</strong>
              </Typography>
            </Box>

            {/* Action Buttons */}
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={handleReset}
                  sx={{
                    borderRadius: '6px',
                    fontWeight: 500,
                  }}
                >
                  Reset
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleApply}
                  sx={{
                    borderRadius: '6px',
                    fontWeight: 500,
                  }}
                >
                  Apply
                </Button>
              </Grid>
            </Grid>
          </Box>
        </motion.div>
      </Popover>
    </>
  );
};

export default GuestSelector;
